import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from '../Button';
import Col from '../Col';
import File from '../File';
import Form, { Heading } from '../Form';
import Input from '../Input';
import Row from '../Row';
import Textarea from '../Textarea';
import { selectIssueTypes } from 'src/store/selectors/formOptions';
import { selectFndFormData } from 'src/store/selectors/fndForm';
import { setFndFormData, clearFndFormData } from 'src/store/actions/fndForm';
import { clearValidation } from 'src/store/actions/validation';
import { selectValidation } from 'src/store/selectors/Validation';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import Loader from '../Loader';
import TextEditor from '../Editor/Editor';
import { convertHTMLToString } from 'src/helpers/validationHelper';
import { onCheckHeicDocumentAndMp4 } from 'src/helpers/CommonHelpers';
import { toast } from 'react-toastify';
import moment from 'moment';
import { upload } from 'src/helpers/uploadDocument';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import 'react-toastify/dist/ReactToastify.css';

interface IFaqFormProps {
  onClose: any;
  onSaveFaq: any;
}

const FaqForm = ({ onClose, onSaveFaq }: IFaqFormProps) => {
  const dispatch = useDispatch();

  // Use selectors to get state from Redux store.
  const issueTypes = useSelector(selectIssueTypes);
  const fndForm = useSelector(selectFndFormData);
  const validation = useSelector(selectValidation);
  const loader = useSelector(selectFormLoader);

  // Initialize state.
  const [editorState, setEditorState] = useState(fndForm.answer);
  const [buttonPressed, setButtonPressed] = useState('');
  const [convertedTextAnswer, setConvertedTextAnswer] = useState(
    fndForm.answer ? fndForm.answer.replace(/<[^>]+>/g, '') : ''
  );

  // Equivalent to componentDidMount.
  useEffect(() => {
    let types = [];
    issueTypes.map((it) => {
      types = [...types, it.name];
    });
    dispatch(setFndFormData({ type: types }));
  }, [dispatch, issueTypes]);

  // Equivalent to componentDidUpdate.
  useEffect(() => {
    if (validation.status === true) {
      if (
        validation.message === 'FaqDocNotes' &&
        buttonPressed === 'SaveAndClose'
      ) {
        onClose();
        dispatch(clearFndFormData());
        dispatch(clearValidation());
      } else if (
        validation.message === 'FaqDocNotes' &&
        buttonPressed === 'SaveAndAdd'
      ) {
        dispatch(clearFndFormData());
        dispatch(clearValidation());
      }
    }
  }, [validation, buttonPressed, onClose, dispatch]);

  const onEditorChange = (editorState: any) => {
    let answer = convertHTMLToString(editorState);

    setEditorState(editorState);
    setConvertedTextAnswer(answer);
  };

  const handleSaveFaq = (actionType: any) => {
    onSaveFaq({ ...fndForm, answer: editorState });
    setButtonPressed(actionType);
    setEditorState('');
    setConvertedTextAnswer('');
  };

  async function onSelectDocument(file) {
    if (!file || file.length === 0) return;

    try {
      const selectedFile = file[0];
      const path = selectedFile.path;
      const isImage: boolean = onUrlImageExtensionExist(path);

      // Create timestamped filename.
      const extension = path.split('.').pop();
      const baseName = path.substring(0, path.lastIndexOf('.'));
      const timestamp = moment().format('MMDDYYYYhmmss');
      const newFileName: string =
        `${baseName}${timestamp}.${extension}`.replace(/ /g, '');

      if (!isImage) {
        toast.success(
          'Your document upload has started, it may take some time.',
          {
            autoClose: 5000,
            position: 'top-center',
          }
        );
      }

      const uploadResult = await upload(selectedFile, newFileName);

      dispatch(
        setFndFormData({
          image: isImage ? file : uploadResult,
          uploadType: isImage ? 'Image' : 'Doc',
        })
      );

      // Confirm success to user.
      if (isImage) {
        toast.success('Image uploaded successfully', {
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('There was an error uploading your file. Please try again.', {
        position: 'top-center',
      });
    }
  }

  const buttonDisabled =
    fndForm.question &&
    fndForm.question.trim() &&
    convertedTextAnswer &&
    convertedTextAnswer.trim();

  return (
    <Form
      headline={(fndForm.id ? 'Edit' : 'Add') + ' a FAQ'}
      onClose={onClose}
      actions={
        <>
          {loader ? <Loader center={true} /> : null}
          {fndForm.id ? (
            <div />
          ) : (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              onClick={() => handleSaveFaq('SaveAndAdd')}
              disabled={!buttonDisabled}
              block={undefined}
              outlined={undefined}
              icon={undefined}
              outline={undefined}
              weight={undefined}
              children={undefined}
              style={undefined}
            />
          )}
          <Button
            color="teal"
            size="small"
            label="Save FAQ"
            onClick={() => handleSaveFaq('SaveAndClose')}
            disabled={!buttonDisabled}
            block={undefined}
            outlined={undefined}
            icon={undefined}
            outline={undefined}
            weight={undefined}
            children={undefined}
            style={undefined}
          />
        </>
      }
      component={
        <>
          <Row chonky>
            <Col chonky xs="12" md="6">
              <Heading>FAQ Details</Heading>
              <Input
                value={fndForm.question}
                onChange={(e) =>
                  dispatch(setFndFormData({ question: e.target.value }))
                }
                label="Question"
                placeholder="eg., What does this error code mean?"
                required
                block
              />
              <div className="input">
                <div className="input__require">
                  <label className="input__label">Answer</label>
                  <div className="input__required" />
                </div>
                <div
                  style={{
                    borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
                  }}
                  className="input__control"
                >
                  <TextEditor
                    editorState={editorState}
                    onEditorStateChange={onEditorChange}
                    readOnly={undefined}
                    placeholder={undefined}
                  />
                </div>
              </div>
              <br />
              <Input
                value={fndForm.issue}
                onChange={(e) =>
                  dispatch(setFndFormData({ issue: e.target.value }))
                }
                label="Category"
                placeholder="eg., Power Issues"
                block
              />
            </Col>
            <Col xs="12" md="6" chonky>
              <Heading>Attachments</Heading>
              <File
                label="File"
                type={['image', 'video', 'pdf']}
                onSelect={(file) =>
                  onCheckHeicDocumentAndMp4(file) && onSelectDocument(file)
                }
                files={
                  fndForm.uploadType === 'Doc'
                    ? []
                    : fndForm.image && fndForm.image.length === 1
                      ? fndForm.image
                      : []
                }
                image={
                  fndForm.uploadType === 'Doc'
                    ? null
                    : fndForm.image && fndForm.image.length > 1
                      ? fndForm.image
                      : null
                }
                required={true}
              />
              {fndForm.uploadType === 'Doc' && fndForm.image ? (
                <a href={fndForm.image} target="_blank">
                  <label style={{ paddingBottom: 5 }}>
                    {fndForm.image.split('.com/')[1]}
                  </label>
                </a>
              ) : null}
              <div
                style={{
                  textAlign: 'center',
                  color: '#43467F',
                  fontWeight: 700,
                }}
              >
                {' '}
                (OR)
              </div>
              <Textarea
                value={fndForm.Embed}
                onChange={(e) =>
                  dispatch(setFndFormData({ Embed: e.target.value }))
                }
                label="Embed "
                block
                type="media"
              />
              <Input
                value={fndForm && fndForm.linkTitle ? fndForm.linkTitle : ''}
                onChange={(e) =>
                  dispatch(setFndFormData({ linkTitle: e.target.value }))
                }
                label="Link Title"
                placeholder="eg., How to fix error codes"
                block
              />
              <Textarea
                value={fndForm && fndForm.caption ? fndForm.caption : ''}
                onChange={(e) =>
                  dispatch(setFndFormData({ caption: e.target.value }))
                }
                label="Caption"
                placeholder="Provide details."
                block
              />
            </Col>
          </Row>
        </>
      }
      footer={undefined}
      onSubmit={undefined}
      strapline={undefined}
      onExit={undefined}
    />
  );
};

export default FaqForm;
